import { useState, useEffect } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { cn } from '../../shared/lib/cn';
import { userFacingLoadError } from '../../shared/lib/staff-user-facing-error';
import { Button, getButtonClassNames } from '../../shared/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Stack } from '../../shared/ui/Stack';
import { getDenyReason, isRouteAllowed } from '../../shared/access/access';
import { toBackendStaffSpaPath } from '../../shared/access/route-bridge';
import {
  canAccessTenantOrganizationUnifiedProfileSurface,
  hasTenantWideStaffGovernanceContourOrSaOrgLift,
  isTenantOrganizationProfileStaffRouteKey,
  isTenantStaffGovernanceKeyedRoute,
  type SuperadminOrgTenantWideUiGateContext
} from '../../shared/access/tenant-staff-governance';
import type { CapabilitiesSnapshot, MeResponse } from '../../entities/me/me-contract.types';
import {
  getDisallowedNoTenantCapsRedirect,
  getNoOrgShellRedirectPath,
  isStaffPlatformSurfaceRoute,
  resolveFirstAllowedRoute,
  resolveStaffPostLoginDestination
} from '../../shared/access/route-resolver';
import {
  isStaffPlatformMode,
  isStaffPlatformModeBlockedFrontendPath,
  staffPlatformModeBlockedRedirectTarget
} from '../../shared/access/staff-platform-mode';
import { useAuth } from '../../shared/auth/use-auth';
import { isBlockingState, isReadyState, type StaffBootstrapState } from '../../shared/bootstrap/bootstrap-state';
import { StaffFullRoutePreloader } from '../../shared/ui/StaffFullRoutePreloader';
import { ROUTE_PATHS } from './route-paths';
import { getMeOnboarding, type OnboardingResponse } from '../../entities/me/me.api';
import AgreementsGatePage from '../../pages/system/AgreementsGatePage';

function normalizedPathname(pathname: string): string {
  const p = pathname.replace(/\/+$/, '') || '/';
  return p;
}

/** Публичный экран входа: при гидрации сессии (`token` есть, `me` ещё грузится) не блокируем форму прелоадером — иначе e2e/пользователь не видит `#login-email`, пока `/auth/me` не ответит. */
function isStaffLoginPath(pathname: string): boolean {
  return normalizedPathname(pathname) === ROUTE_PATHS.login;
}

function routeAccessRedirect(
  pathname: string,
  capabilities: CapabilitiesSnapshot | null | undefined,
  bootstrapState: StaffBootstrapState,
  me: MeResponse | null,
  impersonateOrgId: number | null
) {
  const path = normalizedPathname(pathname);
  /** Витрина UI `/controls` — только в dev-режиме, только режим платформы (СА без контекста организации и без имперсонации). */
  if (import.meta.env.DEV && path === ROUTE_PATHS.controls) {
    if (isStaffPlatformMode(me, impersonateOrgId)) return null;
    return <Navigate to={resolveFirstAllowedRoute(capabilities, me)} replace />;
  }

  if (isStaffPlatformMode(me, impersonateOrgId) && isStaffPlatformModeBlockedFrontendPath(pathname)) {
    return <Navigate to={staffPlatformModeBlockedRedirectTarget()} replace />;
  }

  const noOrg = getNoOrgShellRedirectPath(pathname, bootstrapState.kind, capabilities);
  if (noOrg) return <Navigate to={noOrg} replace />;

  const orgBootstrap =
    bootstrapState.kind === 'authenticated_with_org_context' ||
    bootstrapState.kind === 'impersonating_org';

  const saOrgWideGateCtx: SuperadminOrgTenantWideUiGateContext = { me, orgBootstrap };

  /** Platform shell: отдельного ключа в `routes_allowed` нет — гейт `staff_admin_shell` из `/auth/me`. */
  if (path === ROUTE_PATHS.superadmin) {
    if (!capabilities?.staff_admin_shell?.allowed) {
      return <Navigate to={ROUTE_PATHS.forbidden} replace />;
    }
    if (orgBootstrap) {
      const to = resolveFirstAllowedRoute(capabilities, me);
      if (to !== ROUTE_PATHS.superadmin) {
        return <Navigate to={to} replace />;
      }
    }
    return null;
  }

  const backendKey = toBackendStaffSpaPath(path);
  if (!backendKey) {
    return <Navigate to={resolveFirstAllowedRoute(capabilities, me)} replace />;
  }

  /** `/organization` и legacy `/organization/settings` — один surface: допуск по «другому» ключу в `routes_allowed`. */
  const orgProfileDeepLinkAlias =
    orgBootstrap &&
    (backendKey === '/organization' || backendKey === '/organization/settings') &&
    !isRouteAllowed(capabilities, backendKey) &&
    canAccessTenantOrganizationUnifiedProfileSurface(capabilities, saOrgWideGateCtx);

  /** Хаб «Пользователи» org: допуск по legacy `/all-users`, `/clients`, `/staff` или `/access-rbac`, если снимок без ключа `/users`. */
  const tenantUsersHubDeepLinkAlias =
    orgBootstrap &&
    backendKey === '/users' &&
    !isRouteAllowed(capabilities, '/users') &&
    (isRouteAllowed(capabilities, '/all-users') ||
      isRouteAllowed(capabilities, '/clients') ||
      isRouteAllowed(capabilities, '/staff') ||
      isRouteAllowed(capabilities, '/access-rbac'));

  if (!isRouteAllowed(capabilities, backendKey) && !orgProfileDeepLinkAlias && !tenantUsersHubDeepLinkAlias) {
    if (orgBootstrap && isStaffPlatformSurfaceRoute(backendKey)) {
      return <Navigate to={resolveFirstAllowedRoute(capabilities, me)} replace />;
    }
    const noTenant = getDisallowedNoTenantCapsRedirect(bootstrapState.kind, capabilities);
    if (noTenant) return <Navigate to={noTenant} replace />;
    const deny = getDenyReason(capabilities, backendKey);
    if (deny?.type === 'module_unavailable') {
      return (
        <Navigate
          to={ROUTE_PATHS.moduleUnavailable}
          replace
          state={{ moduleCode: deny.module, from: path }}
        />
      );
    }
    return <Navigate to={ROUTE_PATHS.forbidden} replace state={{ from: path }} />;
  }

  if (orgBootstrap && backendKey === '/superadmin/assistant') {
    return <Navigate to={ROUTE_PATHS.assistant} replace state={{ from: path }} />;
  }

  if (
    orgBootstrap &&
    isTenantStaffGovernanceKeyedRoute(backendKey) &&
    !hasTenantWideStaffGovernanceContourOrSaOrgLift(capabilities, saOrgWideGateCtx)
  ) {
    return <Navigate to={ROUTE_PATHS.forbidden} replace state={{ from: path }} />;
  }

  if (
    orgBootstrap &&
    isTenantOrganizationProfileStaffRouteKey(backendKey) &&
    !canAccessTenantOrganizationUnifiedProfileSurface(capabilities, saOrgWideGateCtx)
  ) {
    return <Navigate to={ROUTE_PATHS.forbidden} replace state={{ from: path }} />;
  }
  return null;
}

export function ProtectedRoute() {
  const { bootstrapState, me, impersonateOrgId, apiClient, refreshMe } = useAuth();
  const location = useLocation();

  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingResponse | null>(null);
  const [checkingAgreements, setCheckingAgreements] = useState(false);
  const [agreementsChecked, setAgreementsChecked] = useState(false);

  const userId = me?.id;
  const orgId = impersonateOrgId ?? me?.organization_id ?? null;

  useEffect(() => {
    if (!isReadyState(bootstrapState) || !userId) {
      setOnboardingStatus(null);
      setAgreementsChecked(false);
      return;
    }

    let active = true;
    const check = async () => {
      setCheckingAgreements(true);
      try {
        const res = await getMeOnboarding(apiClient, orgId);
        if (active) {
          setOnboardingStatus(res);
          setAgreementsChecked(true);
        }
      } catch {
        if (active) {
          setAgreementsChecked(true);
        }
      } finally {
        if (active) {
          setCheckingAgreements(false);
        }
      }
    };

    void check();

    return () => {
      active = false;
    };
  }, [bootstrapState, userId, orgId, apiClient]);

  if (bootstrapState.kind === 'loading') {
    return <StaffFullRoutePreloader label="Загрузка сессии…" />;
  }

  if (bootstrapState.kind === 'anonymous') {
    return <Navigate to={ROUTE_PATHS.login} replace state={{ from: location }} />;
  }

  if (isBlockingState(bootstrapState)) {
    if (bootstrapState.kind === 'forbidden') {
      return <Navigate to={ROUTE_PATHS.forbidden} replace />;
    }
    if (bootstrapState.kind === 'module_unavailable') {
      return (
        <Navigate
          to={ROUTE_PATHS.moduleUnavailable}
          replace
          state={{
            moduleCode: bootstrapState.moduleCode
          }}
        />
      );
    }
    if (bootstrapState.kind === 'bootstrap_error') {
      const summary = bootstrapState.message
        ? userFacingLoadError(new Error(bootstrapState.message))
        : 'Не удалось загрузить данные.';
      return (
        <div className="staff-system-shell" data-testid="in-place-bootstrap-error">
          <Card className="staff-system-inner">
            <CardHeader>
              <CardTitle>Ошибка связи</CardTitle>
              <CardDescription>
                Не удалось обновить данные сессии. Страница сохранена — вы можете повторить попытку прямо сейчас.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Stack gap={4}>
                <p className="staff-m-0 text-sm text-[hsl(var(--ui-muted-foreground))]">{summary}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => void refreshMe()}
                  >
                    Повторить попытку
                  </Button>
                  <Link
                    to={ROUTE_PATHS.login}
                    className={cn(getButtonClassNames({ variant: 'secondary' }), 'staff-link-button')}
                  >
                    К входу
                  </Link>
                </div>
              </Stack>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  if (!isReadyState(bootstrapState)) {
    return <Navigate to={ROUTE_PATHS.login} replace />;
  }

  if (checkingAgreements) {
    return <StaffFullRoutePreloader label="Проверка соглашений…" />;
  }

  const needAgreements = onboardingStatus
    ? onboardingStatus.need_personal_data_agreement ||
      onboardingStatus.need_crm_agreement ||
      onboardingStatus.need_org_agreement
    : false;

  if (agreementsChecked && needAgreements && onboardingStatus) {
    return (
      <AgreementsGatePage
        onboarding={onboardingStatus}
        onSuccess={() => {
          const check = async () => {
            try {
              const res = await getMeOnboarding(apiClient, orgId);
              setOnboardingStatus(res);
            } catch {
              // ошибка перезагрузки статуса соглашений — не критична, пользователь может повторить
            }
          };
          void check();
        }}
      />
    );
  }

  const redirect = routeAccessRedirect(
    location.pathname,
    me?.capabilities ?? null,
    bootstrapState,
    me,
    impersonateOrgId
  );
  if (redirect) return redirect;

  // auth-methods-uniform (правила 5/6): вход только после подтверждения email.
  if (me && !me.email_verified_at && me.email_verification_available) {
    return <Navigate to={ROUTE_PATHS.confirmEmail} replace />;
  }

  return <Outlet />;
}


export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { bootstrapState, me } = useAuth();
  const location = useLocation();

  if (bootstrapState.kind === 'loading' && !isStaffLoginPath(location.pathname)) {
    return <StaffFullRoutePreloader label="Проверка сессии…" />;
  }

  if (isReadyState(bootstrapState) && !isBlockingState(bootstrapState)) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    const dest = resolveStaffPostLoginDestination(from, me?.capabilities ?? null, me);
    return <Navigate to={dest} replace />;
  }

  return <>{children}</>;
}

export function SystemMessageLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="staff-system-shell">
      <div className="staff-system-inner">{children}</div>
    </div>
  );
}
